<template>
  <div>
    <CustomModal
      :size="'lg'"
      :theme="'tfm-primary'"
      :modalTitle="resultModal.modalTitle"
      :modalColor="resultModal.modalColor"
      :isShowPopup="resultModal.isShowPopup"
      :buttons="resultModal.buttons"
      :closeOnBackdrop="false"
      @modalCallBack="resultModalCallBack"
    >
      <template v-slot:body>
        <h4 class="text-center mb-3">
          🎉 Congratulations, {{ candidateProfile?.full_name | toTitleCase }}
        </h4>
        <p class="mb-2">
          You’ve successfully completed the English Language Assessment. Based
          on your performance, your results are:
        </p>

        <table class="table table-sm mb-0">
          <tbody>
            <tr>
              <td class="text-start w-50"><strong>Total Questions:</strong></td>
              <td class="text-start w-50">{{ results.total }}</td>
            </tr>
            <tr>
              <td class="text-start w-50"><strong>Correct Answers:</strong></td>
              <td class="text-start w-50">{{ results.correct }}</td>
            </tr>
            <tr>
              <td class="text-start w-50"><strong>Score:</strong></td>
              <td class="text-start w-50">{{ results.score }}%</td>
            </tr>
            <tr>
              <td class="text-start w-50 align-middle">
                <strong>CEFR:</strong>
                <CIcon
                  class="ml-1 text-muted"
                  name="cil-info"
                  style="cursor: pointer"
                  v-c-popover="cefrPopoverOptions"
                />
              </td>
              <td class="text-start w-50">
                Level: {{ results.cefr }} - {{ results.level }}
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </CustomModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CustomModal from "../../components/reusable/CustomModal.vue";

export default {
  components: {
    CustomModal,
  },
  data() {
    return {
      resultModal: {
        modalTitle: "",
        isShowPopup: true,
        buttons: [{ label: "Ok", type: "secondary" }],
      },
      results: {},
      grade_data: [
        {
          min: 0,
          max: 9,
          cefr: "A1",
          level: "Beginner",
          description: "Basic phrases, limited understanding",
        },
        {
          min: 10,
          max: 19,
          cefr: "A2",
          level: "Elementary",
          description: "Simple communication in familiar areas",
        },
        {
          min: 20,
          max: 29,
          cefr: "B1",
          level: "Intermediate",
          description: "Can handle everyday situations",
        },
        {
          min: 30,
          max: 39,
          cefr: "B2",
          level: "Upper Intermediate",
          description: "Good grasp of language, some complexity",
        },
        {
          min: 40,
          max: 49,
          cefr: "C1",
          level: "Advanced",
          description: "Fluent, nuanced understanding",
        },
        {
          min: 50,
          max: 60,
          cefr: "C2",
          level: "Proficient",
          description: "Near-native fluency",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["candidateProfile", "getCandidateAssessmentResult"]),

    cefrPopoverOptions() {
      return {
        header: "CEFR Levels",
        content: () => this.gradePopoverHtml(),
        html: true,
        placement: "bottom",
        trigger: "click focus hover",
        boundariesElement: "viewport",
        offset: "0,8",
        container: "body",
        customClass: "cefr-popover",
        sanitize: false,
      };
    },
  },
  methods: {
    ...mapActions(["getAssessmentTestByCandidateId"]),

    getCefrBand(score) {
      const s = Math.max(0, Math.min(60, Number(score) || 0));
      return (
        this.grade_data.find((b) => s >= b.min && s <= b.max) ||
        this.grade_data[0]
      );
    },

    async loadAssessmentResults() {
      await this.getAssessmentTestByCandidateId();

      const total = 60;
      const correct =
        Number(this.getCandidateAssessmentResult?.total_score) || 0;
      const { cefr, level, description } = this.getCefrBand(correct);
      const score = total ? Math.round((correct / total) * 100) : 0;

      this.results = { total, correct, score, cefr, level, description };
    },

    resultModalCallBack() {
      this.resultModal.isShowPopup = false;
      this.$emit("closeModal");
    },

    gradePopoverHtml() {
      const score = Number(this.results?.correct) || 0;

      const rows = this.grade_data
        .map((b) => {
          const isActive = score >= b.min && score <= b.max;
          const trClass = isActive ? 'class="active-row"' : "";
          return `
            <tr ${trClass}>
              <td class="num pr-2"><strong>${b.min} – ${b.max}</strong></td>
              <td class="lvl pr-2">${b.cefr} – ${b.level}</td>
              <td class="desc">${b.description}</td>
            </tr>
          `;
        })
        .join("");

      return `
        <div class="cefr-table-wrap">
          <table class="table table-sm mb-0 cefr-table">
            <thead>
              <tr>
                <th class="num">Correct Answers</th>
                <th class="lvl">CEFR Level</th>
                <th class="desc">Description</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
    },
  },

  async mounted() {
    await this.loadAssessmentResults();
  },

  filters: {
    toTitleCase(data) {
      return data ? _.startCase(data) : data;
    },
  },
};
</script>

<style>
.cefr-popover.popover,
.popover-old,
.bs-popover-old-auto,
.popover {
  max-width: none !important;
  width: auto !important;
  display: inline-block !important;
  z-index: 9999 !important;
  white-space: normal !important;
}

.popover-old,
.bs-popover-old-auto {
  max-width: none !important;
  width: auto !important;
  display: inline-block !important;
  z-index: 9999 !important;
}

.cefr-popover .popover-body,
.popover-old .popover-body,
.bs-popover-old-auto .popover-body {
  padding: 0 !important;
}

.cefr-table {
  margin: 0;
  min-width: 220px;
  table-layout: auto;
}
.cefr-table th,
.cefr-table td {
  vertical-align: top;
}
.cefr-table th.num,
.cefr-table td.num {
  white-space: nowrap;
  width: 1%;
}
.cefr-table th.lvl,
.cefr-table td.lvl {
  white-space: nowrap;
  width: 1%;
}
.cefr-table th.desc,
.cefr-table td.desc {
  white-space: normal;
}

.cefr-table tr.active-row {
  background: #f5f4ff;
  font-weight: 600;
}

.cefr-table-wrap {
  max-height: 340px;
  overflow: auto;
}
</style>
